import React from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@tempmee/react';
import { OutlineLocationMarkerIcon, OutlinePencilIcon } from '@tempmee/react-icons';
import { TableCardFrame } from '../../../commonComponents/TableCardFrame';
import { UNKNOWN_ADMIN_LABEL, salesMetroRowPropType } from '../constants';
import styles from './SalesMetrosTable.module.scss';

const COLUMNS = [
  { label: 'Metro Name', className: styles.nameColumn },
  { label: 'Assigned Admin' },
  { label: 'Zip', align: 'center', className: styles.iconColumn },
  { label: 'Edit', align: 'center', className: styles.iconColumn },
];

const adminNames = (admins) =>
  admins.map((admin) => admin.name ?? UNKNOWN_ADMIN_LABEL).join(', ');

/**
 * The Sales Metros list, in the shared `TableCardFrame`. `title={null}`
 * (the frame's default) for the reason GotuAdminsTable gives: the page header
 * already names it.
 */
export const SalesMetrosTable = ({
  metros, isPending, isError, hasSearch, onViewZips, onEditAdmins, footer,
}) => (
  <TableCardFrame
    columns={COLUMNS}
    rowCount={metros.length}
    isPending={isPending}
    isError={isError}
    emptyMessage={hasSearch ? 'No sales metros match this search.' : 'No sales metros yet.'}
    errorMessage="Could not load sales metros."
    footer={footer}
  >
    {metros.map((metro) => (
      <TableRow key={metro.id} hover>
        <TableCell>
          {/* `title` keeps the full name reachable once the cell cuts it short. */}
          <Typography
            variant="body1"
            bold
            className={`text-text-secondary ${styles.name}`}
            title={metro.name}
          >
            {metro.name}
          </Typography>
        </TableCell>
        <TableCell>
          {metro.admins.length > 0 ? (
            adminNames(metro.admins)
          ) : (
            <Typography variant="body1" italic className="text-text-disabled">
              No Admin Assigned
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <IconButton
            Icon={<OutlineLocationMarkerIcon aria-hidden />}
            appearance="default"
            aria-label={`View zip codes for ${metro.name}`}
            onClick={() => onViewZips(metro)}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton
            Icon={<OutlinePencilIcon aria-hidden />}
            appearance="default"
            aria-label={`Edit admins for ${metro.name}`}
            onClick={() => onEditAdmins(metro)}
          />
        </TableCell>
      </TableRow>
    ))}
  </TableCardFrame>
);

SalesMetrosTable.propTypes = {
  metros: PropTypes.arrayOf(salesMetroRowPropType).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  /** Picks the empty copy: "nothing matched" reads as a bug with no search set. */
  hasSearch: PropTypes.bool,
  /** Called with the row's metro when its zip icon is pressed. */
  onViewZips: PropTypes.func.isRequired,
  /** Called with the row's metro when its edit icon is pressed. */
  onEditAdmins: PropTypes.func.isRequired,
  /** Footer slot — the page passes its pagination. */
  footer: PropTypes.node,
};

SalesMetrosTable.defaultProps = { hasSearch: false, footer: null };

export default SalesMetrosTable;
